import { SignUp } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormCheckbox } from '@components/common/FormCheckbox/FormCheckbox';
import { FormControlLabel } from '@components/common/FormControlLabel';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageURL } from '@router/pageURL';
import { gbPhoneNumberRhfValidate } from '@utils/gbPhoneNumber';
import { userApi } from '@api/user/userApi';
import { IUpdateUserAgreementAndPolicyDto } from '@api/user/types';
import { ClerkWrapper } from '@components/ClerkWrapper';
import { BROKER_ONBOARDING_QUERY_KEY } from '@queries/broker/useBrokerOnboarding';

export const CreateAccountPage = () => {
  return (
    <main className={'flex justify-center'}>
      <SignUp />
    </main>
  );
};

const textFieldIn = {
  type: 'text' as const,
  inputProps: { sx: { padding: '6px !important' } as const, className: 'text-sm font-normal!' as const },
};

export const AgreementPage = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserAgreementAndPolicy, isPending } = useMutation(userApi.updateUserAgreementAndPolicy);

  const form = useForm<IUpdateUserAgreementAndPolicyDto>({
    defaultValues: {
      introducerAgreement: false,
      privacyPolicy: false,
      marketingCommunication: false,
      phoneNumber: '',
    },
  });

  const formState = form.watch();
  const required = intl.formatMessage({ id: 'common.error.requiredField' });

  const submitFormHandler = async (formData: IUpdateUserAgreementAndPolicyDto) => {
    await updateUserAgreementAndPolicy({
      ...formData,
      phoneNumber: formData.phoneNumber.trim(),
    });

    await queryClient.invalidateQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY] });
    await queryClient.refetchQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY], type: 'active' });
  };

  return (
    <ClerkWrapper>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitFormHandler)} className='space-y-6 px-8'>
          <div>
            <h2 className='text-md font-semibold mb-4 text-center'>
              <FormattedMessage id={'auth.signup.postSignupPage.title'} />
            </h2>
            <div className='mb-4'>
              <FormattedMessage id={'auth.signup.phoneNumber'} />
              <FormTextField
                name={'phoneNumber'}
                rules={{
                  required: { value: true, message: required },
                  validate: gbPhoneNumberRhfValidate(intl.formatMessage({ id: 'common.error.validPhoneNumber' })),
                }}
                textFieldProps={{
                  placeholder: intl.formatMessage({ id: 'placeholders.personalDetails.phoneNumber' }),
                  ...textFieldIn,
                }}
              />
            </div>
            <div className='space-y-1'>
              <FormControlLabel
                className='items-center'
                data-testid='policy-introducerAgreement'
                control={<FormCheckbox name={'introducerAgreement'} rules={{ required: true }} />}
                label={
                  <span className='text-sm text-gray-700 ml-2'>
                    <FormattedMessage
                      id={'auth.signup.introducerAgreement'}
                      values={{
                        a: (text: ReactNode[]) => (
                          <a
                            href={PageURL.INTRODUCER_AGREEMENT}
                            target={'_blank'}
                            rel={'noreferrer'}
                            className='text-green-600 hover:text-green-700 underline'
                          >
                            {text}
                          </a>
                        ),
                      }}
                    />
                  </span>
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    paddingTop: '2px',
                  },
                }}
              />
              <FormControlLabel
                className='items-center'
                data-testid='policy-privacyPolicy'
                control={<FormCheckbox name={'privacyPolicy'} rules={{ required: true }} />}
                label={
                  <span className='text-sm text-gray-700 ml-2'>
                    <FormattedMessage
                      id={'auth.signup.privacyPolicy'}
                      values={{
                        a: (text: ReactNode[]) => (
                          <a
                            href={PageURL.PRIVACY_POLICY}
                            target={'_blank'}
                            rel={'noreferrer'}
                            className='text-green-600 hover:text-green-700 underline'
                          >
                            {text}
                          </a>
                        ),
                      }}
                    />
                  </span>
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    paddingTop: '2px',
                  },
                }}
              />
            </div>
          </div>

          <div className='border-t'>
            <h2 className='text-md font-semibold mb-4 text-center'>
              <FormattedMessage id={'auth.signup.marketingPreferences.title'} />
            </h2>
            <FormControlLabel
              control={<FormCheckbox name={'marketingCommunication'} />}
              label={
                <span className='text-sm text-gray-700 ml-2'>
                  <FormattedMessage id={'auth.signup.marketingCommunication'} />
                </span>
              }
              sx={{
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  paddingTop: '8px',
                },
              }}
            />
          </div>

          <div className='flex flex-col gap-3 pt-6'>
            <LoadingButton
              isLoading={isPending}
              buttonProps={{
                variant: 'contained',
                type: 'submit',
                className: 'h-8',
                disabled: !formState.introducerAgreement || !formState.privacyPolicy,
              }}
            >
              <FormattedMessage id={'cta.continue'} />
            </LoadingButton>
          </div>
        </form>
      </FormProvider>
    </ClerkWrapper>
  );
};
